import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { InvoiceStatusStats, InvoiceStatus } from '../types';

interface InvoiceStatsBarProps {
  stats: InvoiceStatusStats[];
  className?: string;
}

const statusConfig: Record<
  InvoiceStatus | 'total',
  { label: string; valueColor?: string }
> = {
  preparation: {
    label: 'Preparation',
    valueColor: 'text-gray-900',
  },
  sent: {
    label: 'Sent',
    valueColor: 'text-blue-600',
  },
  paid: {
    label: 'Paid',
    valueColor: 'text-green-600',
  },
  expired: {
    label: 'Expired',
    valueColor: 'text-orange-600',
  },
  cancelled: {
    label: 'Cancelled',
    valueColor: 'text-red-600',
  },
  total: {
    label: 'Total',
    valueColor: 'text-gray-900',
  },
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function InvoiceStatsBar({ stats, className }: InvoiceStatsBarProps) {
  // Order: preparation, sent, expired, paid, total (exclude cancelled from summary)
  // This matches the Legacy frontend which shows Expired as a separate status
  const orderedStatuses: Array<InvoiceStatus | 'total'> = [
    'preparation',
    'sent',
    'expired',
    'paid',
    'total',
  ];

  const orderedStats = orderedStatuses
    .map((status) => {
      const stat = stats.find((s) => s.status === status);
      if (!stat) return null;
      return { ...stat, config: statusConfig[status] };
    })
    .filter(Boolean);

  if (orderedStats.length === 0) {
    return null;
  }

  return (
    <div className={cn('grid gap-4 md:grid-cols-5', className)}>
      {orderedStats.map((stat) => {
        if (!stat) return null;
        return (
          <Card key={stat.status}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.config.label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={cn('text-2xl font-bold', stat.config.valueColor)}>
                {stat.quantity.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(stat.amount)}
              </p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
